import { useCallback, useEffect, useRef, useState } from 'react'
import { contact, navigation } from '../data/site'
import { ANALYTICS_EVENTS, pushAnalyticsEvent } from '../lib/analytics'

function ArrowIcon() {
  return (
    <svg viewBox="0 0 20 20" aria-hidden="true">
      <path d="M4 10h11M11 5l5 5-5 5" />
    </svg>
  )
}

export default function Header({ currentPath }) {
  const [isOpen, setIsOpen] = useState(false)
  const toggleRef = useRef(null)
  const panelRef = useRef(null)

  const trackMenuState = (state) => {
    pushAnalyticsEvent(ANALYTICS_EVENTS.MENU_TOGGLE, {
      click_location: 'header',
      menu_name: 'mobile_navigation',
      menu_state: state,
    })
  }

  const toggleMenu = () => {
    setIsOpen((currentState) => {
      const nextState = !currentState
      trackMenuState(nextState ? 'open' : 'closed')
      return nextState
    })
  }

  const closeMenu = useCallback((restoreFocus = false) => {
    setIsOpen((currentState) => {
      if (!currentState) return currentState
      trackMenuState('closed')
      return false
    })
    if (restoreFocus) requestAnimationFrame(() => toggleRef.current?.focus())
  }, [])

  useEffect(() => {
    document.body.classList.toggle('menu-open', isOpen)
    if (isOpen) requestAnimationFrame(() => panelRef.current?.querySelector('a')?.focus())

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') closeMenu(true)
    }
    const desktopQuery = window.matchMedia('(min-width: 981px)')
    const handleDesktop = (event) => { if (event.matches) closeMenu() }
    document.addEventListener('keydown', handleKeyDown)
    desktopQuery.addEventListener('change', handleDesktop)

    return () => {
      document.body.classList.remove('menu-open')
      document.removeEventListener('keydown', handleKeyDown)
      desktopQuery.removeEventListener('change', handleDesktop)
    }
  }, [closeMenu, isOpen])

  return (
    <>
      <header className="site-header" data-header>
        <a className="brand" href="/" aria-label="CRS Cimentaciones, inicio" data-analytics-location="header_logo" data-analytics-label="CRS Cimentaciones">
          <img className="brand-logo" src="/images/brand/logo-crs.webp" alt="CRS Cimentaciones" />
          <img className="brand-icon" src="/images/brand/crs-icon.webp" alt="" aria-hidden="true" />
        </a>

        <button
          ref={toggleRef}
          className="menu-toggle"
          type="button"
          aria-expanded={isOpen}
          aria-controls="mobile-navigation"
          onClick={toggleMenu}
          data-analytics-manual="true"
        >
          <span>{isOpen ? 'Cerrar' : 'Menú'}</span>
          <span className="menu-lines" aria-hidden="true"><i /><i /></span>
        </button>

        <nav id="desktop-navigation" className="desktop-nav" aria-label="Navegación principal">
          {navigation.map((item) => {
            const active = item.href === '/' ? currentPath === '/' : currentPath.startsWith(item.href)
            return <a key={item.href} href={item.href} aria-current={active ? 'page' : undefined} data-analytics-location="desktop_navigation">{item.label}</a>
          })}
        </nav>

        <a className="header-cta" href="/contacto/#cotizar" data-analytics-event="cta_click" data-analytics-location="header_cta" data-analytics-label="Ir al formulario de cotización">
          Cotizar <ArrowIcon />
        </a>
      </header>

      <div id="mobile-navigation" ref={panelRef} className={`mobile-panel ${isOpen ? 'is-open' : ''}`} role="dialog" aria-modal={isOpen ? 'true' : undefined} aria-label="Menú principal" aria-hidden={!isOpen} inert={!isOpen}>
        <div className="mobile-panel-inner">
          <p className="mobile-panel-label">Navegación / CRS</p>
          <nav aria-label="Navegación móvil">
            {navigation.map((item, index) => {
              const active = item.href === '/' ? currentPath === '/' : currentPath.startsWith(item.href)
              return (
                <a key={item.href} href={item.href} aria-current={active ? 'page' : undefined} onClick={() => closeMenu()} data-analytics-location="mobile_navigation">
                  <span>{String(index + 1).padStart(2, '0')}</span>{item.label}
                </a>
              )
            })}
            <a href="/contacto/#cotizar" onClick={() => closeMenu()} data-analytics-location="mobile_navigation"><span>{String(navigation.length + 1).padStart(2, '0')}</span>Cotizar</a>
          </nav>
          <div className="mobile-contact">
            <span>Contacto directo</span>
            <a href={contact.phoneHref}>{contact.phoneLabel}</a>
            <a href={`mailto:${contact.email}`}>{contact.email}</a>
          </div>
        </div>
      </div>
    </>
  )
}
